import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Operation, OperationChain, Point, Scenario, TSP } from '@/types';
import { colors, generateColor } from '../../utils/colors';
import { numberFormatter, priceFormatter } from '../../helpers/formatters';
import { IconButton } from '../../helpers/IconButton';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DotsVerticalIcon,
  EyeIcon,
  EyeOffIcon,
  FilterIcon,
  ZoomInIcon,
} from '@iconicicons/react';
import { Circle } from '../../helpers/Circle';
import { MarketBadge, MarketBadgeType } from '../../helpers/MarketBadge';
import { UncontrolledTooltip } from 'reactstrap';
import { ChainDetail } from './ChainDetail';

export const OPTIMIZATION_CHAINS_TABLE_HEADERS = [
  'Actions',
  'Chain',
  'Description',
  'Receipt',
  'Delivery',
  // 'Route',
  'Buy',
  'Sell',
  // 'Transport',
  'Volume',
  'Profit',
  '$/Dth',
  'Details',
];

export type OptimizationChainsTableRowProps = {
  scenario: Scenario;
  tsps: Record<string, TSP>;
  chain: OperationChain;
  points: Record<string, Point>;
  chainNumber: number;
  isSelected: boolean;
  isVisible: boolean;
  onZoomIn: (pathId: number) => void;
  onToggleVisibility: (pathId: number) => void;
  onFilterTsps: (chain: OperationChain) => void;
  onSelectChain: (chainId: number) => void;
  onCopyToClipboard: (chain: OperationChain) => void;
};

export const OptimizationChainsTableRow: React.FC<
  OptimizationChainsTableRowProps
> = ({
  scenario,
  // tsps,
  chain,
  chainNumber,
  isSelected,
  isVisible,
  onZoomIn,
  onToggleVisibility,
  onFilterTsps,
  onSelectChain,
  onCopyToClipboard,
}) => {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [_showDetails, setShowDetails] = useState(false);
  const showDetails = _showDetails || isSelected;
  const chainOperations = useMemo(() => {
    return chain.chain_links
      .map((link) =>
        scenario.operations.find((op) => op.id === link.operation_id),
      )
      .filter(Boolean) as Operation[];
  }, [scenario.operations, chain.chain_links]);

  const receiptOp = useMemo(() => {
    return chainOperations[0];
  }, [chainOperations]);

  const deliveryOp = useMemo(() => {
    return chainOperations[chainOperations.length - 1];
  }, [chainOperations]);

  const chainColor = generateColor(chain.id, 100, 50);

  const handleZoomIn = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onZoomIn(chain.id);
    },
    [onZoomIn, chain.id],
  );

  const handleToggleVisibility = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onToggleVisibility(chain.id);
    },
    [onToggleVisibility, chain.id],
  );

  const handleFilterTsps = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onFilterTsps(chain);
    },
    [onFilterTsps, chain],
  );

  const handleSelectChain = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onSelectChain(chain.id);
    },
    [onSelectChain, chain.id],
  );

  const handleCopyToClipboard = useCallback(
    (evt: React.MouseEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onCopyToClipboard(chain);
    },
    [onCopyToClipboard, chain],
  );

  const toggleDetails = useCallback((evt: React.MouseEvent) => {
    evt.stopPropagation();
    evt.preventDefault();
    setShowDetails((prev) => !prev);
  }, []);

  const cellStyle = useMemo<React.CSSProperties>(() => {
    return showDetails ? { borderBottom: '0px solid transparent' } : {};
  }, [showDetails]);

  return (
    <>
      <tr ref={rowRef}>
        <td style={cellStyle}>
          <div
            className="d-flex gap-1 justify-content-start align-items-center"
            style={{ height: '100%' }}
          >
            <div className="dropend">
              <DotsVerticalIcon
                type="button"
                className="dropdown-toggle"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              />
              <ul className="dropdown-menu">
                <li>
                  <span
                    role="button"
                    className="dropdown-item"
                    onClick={handleCopyToClipboard}
                  >
                    Copy to clipboard
                  </span>
                </li>
                <li>
                  <span role="button" className="dropdown-item">
                    Add annotation
                    {chain.chain_info.has_annotations && (
                      <Circle
                        color={colors.matteRed}
                        size={8}
                        className="ms-2"
                      />
                    )}
                  </span>
                </li>
              </ul>
            </div>
            <IconButton
              onClick={handleZoomIn}
              disabled={chainOperations.every(
                (op) => op?.path_node_ids.length < 2,
              )}
              disabledClassName="text-muted"
              icon={ZoomInIcon}
            />
            <>
              {isSelected && (
                <UncontrolledTooltip target={`toggle-visibility-${chain.id}`}>
                  Selected path is always visible
                </UncontrolledTooltip>
              )}
              <IconButton
                id={`toggle-visibility-${chain.id}`}
                onClick={handleToggleVisibility}
                disabledClassName="text-muted"
                disabled={isSelected}
                icon={isVisible || isSelected ? EyeIcon : EyeOffIcon}
              />
            </>
            <IconButton onClick={handleFilterTsps} icon={FilterIcon} />
          </div>
        </td>
        <td style={cellStyle}>
          <div
            onClick={handleSelectChain}
            role="button"
            className={isSelected ? 'fw-bold' : ''}
          >
            <Circle color={chainColor} className="me-2" />
            <span>Chain {chainNumber}</span>
            <br />
            <span className="text-muted fs-sm">#{chain.id}</span>
          </div>
        </td>
        <td style={{ wordWrap: 'break-word', maxWidth: 256, ...cellStyle }}>
          {chain.chain_info?.name ?? ''}
        </td>
        <td style={cellStyle}>
          {receiptOp?.receipt_point && (
            <MarketBadge
              point={receiptOp.receipt_point}
              assetName={
                receiptOp.receipt_asset_name ?? receiptOp.receipt_point.name
              }
              type={MarketBadgeType.BUY}
              price={receiptOp.receipt_asset_price}
              volume={receiptOp.received_dth}
            />
          )}
        </td>
        <td style={cellStyle}>
          {deliveryOp?.receipt_point && (
            <MarketBadge
              point={deliveryOp.receipt_point}
              assetName={
                deliveryOp.delivery_asset_name ?? deliveryOp.delivery_point.name
              }
              type={MarketBadgeType.SELL}
              price={deliveryOp.delivery_asset_price}
              volume={deliveryOp.delivered_dth}
            />
          )}
        </td>
        <td style={cellStyle}>
          {priceFormatter.format(receiptOp?.receipt_asset_price ?? 0)}
        </td>
        <td style={cellStyle}>
          {priceFormatter.format(deliveryOp?.delivery_asset_price ?? 0)}
        </td>
        <td style={cellStyle}>{numberFormatter(chain.delivered_dth)} dth</td>
        <td style={cellStyle}>{priceFormatter.format(chain.profit)}</td>
        <td style={cellStyle}>
          {priceFormatter.format(chain.profit / chain.delivered_dth)}
        </td>
        <td style={cellStyle}>
          <div
            className="d-flex justify-content-center"
            role="button"
            onClick={toggleDetails}
          >
            {showDetails ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        </td>
      </tr>
      {showDetails && (
        <tr>
          <td
            colSpan={OPTIMIZATION_CHAINS_TABLE_HEADERS.length}
            className="m-0 b-0 p-0"
          >
            <div className="w-100 py-2 px-5">
              <ChainDetail
                id={chain.id.toString()}
                scenario={scenario}
                chain={chain}
                chainNumber={chainNumber}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
